import { useMemo, useState } from 'react';
import type { WebhookEventRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useMarkWebhookProcessed,
  useWebhookEvents,
  type WebhookListFilters,
} from '../api/webhookEvents.ts';
import {
  formatLocalTimestamp,
  localDateTimeInputValueToIso,
} from '../components/formatters.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * `/webhook-events` — observability for the generic webhook inbox.
 * Epic #109 P3.16.
 *
 * Filters by provider, processed flag, and a since-timestamp. Shows
 * newest-first table with a `Mark processed` action on pending rows.
 * `payload` is collapsed into a truncated monospaced block; operators
 * expand via native `<details>` without leaving the page.
 */

type ProcessedFilter = 'all' | 'true' | 'false';

function toProcessedFilter(value: string): ProcessedFilter {
  if (value === 'true' || value === 'false') {
    return value;
  }
  return 'all';
}

export function WebhookEventsPage(): JSX.Element {
  const t = useT();
  const [provider, setProvider] = useState('');
  const [processed, setProcessed] = useState<ProcessedFilter>('all');
  const [since, setSince] = useState('');

  const filters = useMemo<WebhookListFilters>(() => {
    return {
      ...(provider.length > 0 ? { provider } : {}),
      ...(processed !== 'all' ? { processed } : {}),
      ...(since.length > 0 ? { since: localDateTimeInputValueToIso(since) } : {}),
    };
  }, [provider, processed, since]);

  const query = useWebhookEvents(filters);

  return (
    <section className="flex flex-col gap-6" data-testid="webhook-events-page">
      <PageHeader title={t('webhook_events.title')} sub={t('webhook_events.subtitle')} />

      <div
        data-testid="webhook-events-filters-card"
        className="flex flex-wrap items-end gap-4 rounded-lg border border-line-soft bg-card p-4"
      >
        <FormField label={t('webhook_events.filter.provider')} fieldId="webhook-events-filter-provider">
          <Input
            id="webhook-events-filter-provider"
            type="text"
            value={provider}
            onChange={(e) => setProvider(e.currentTarget.value)}
            data-testid="webhook-events-filter-provider"
            placeholder={t('webhook_events.filter.provider_placeholder')}
            className="min-w-[180px]"
          />
        </FormField>
        <FormField label={t('webhook_events.filter.processed')} fieldId="webhook-events-filter-processed">
          <Select
            value={processed}
            onValueChange={(v) => setProcessed(toProcessedFilter(v))}
            ariaLabel={t('webhook_events.filter.processed')}
            testId="webhook-events-filter-processed"
            className="min-w-[160px]"
            options={[
              { value: 'all', label: t('webhook_events.filter.processed.all') },
              { value: 'false', label: t('webhook_events.filter.processed.pending') },
              { value: 'true', label: t('webhook_events.filter.processed.done') },
            ]}
          />
        </FormField>
        <FormField label={t('webhook_events.filter.since')} fieldId="webhook-events-filter-since">
          <Input
            id="webhook-events-filter-since"
            type="datetime-local"
            value={since}
            onChange={(e) => setSince(e.currentTarget.value)}
            data-testid="webhook-events-filter-since"
            className="min-w-[220px]"
          />
        </FormField>
      </div>

      <div data-testid="webhook-events-list-card" className="flex flex-col gap-4">
        {query.isLoading ? (
          <p
            className="m-0 rounded-lg border border-line-soft bg-card px-4 py-8 text-center text-dense text-paper-low"
            data-testid="webhook-events-loading"
          >
            {t('webhook_events.loading')}
          </p>
        ) : query.isError ? (
          <p
            className="m-0 rounded-lg border border-bad-line bg-bad-dim px-4 py-4 text-dense text-bad-bright"
            role="alert"
            data-testid="webhook-events-error"
          >
            {t('webhook_events.error')}
          </p>
        ) : (query.data ?? []).length === 0 ? (
          <div className="rounded-lg border border-line-soft bg-card">
            <EmptyState testId="webhook-events-empty" title={t('webhook_events.empty')} />
          </div>
        ) : (
          <EventsTable events={query.data ?? []} />
        )}
      </div>
    </section>
  );
}

interface EventsTableProps {
  readonly events: readonly WebhookEventRecord[];
}

function EventsTable({ events }: EventsTableProps): JSX.Element {
  const t = useT();
  return (
    <Table testId="webhook-events-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('webhook_events.column.created_at')}</Th>
          <Th>{t('webhook_events.column.provider')}</Th>
          <Th>{t('webhook_events.column.event_type')}</Th>
          <Th>{t('webhook_events.column.status')}</Th>
          <Th>{t('webhook_events.column.payload')}</Th>
          <Th className="text-right">{t('webhook_events.column.actions')}</Th>
        </Tr>
      </THead>
      <TBody>
        {events.map((ev) => (
          <EventRow key={ev.id} event={ev} />
        ))}
      </TBody>
    </Table>
  );
}

interface EventRowProps {
  readonly event: WebhookEventRecord;
}

function EventRow({ event }: EventRowProps): JSX.Element {
  const t = useT();
  const mark = useMarkWebhookProcessed();
  const toast = useToast();
  const isProcessed = event.processed_at !== null;

  async function handleMark(): Promise<void> {
    try {
      await mark.mutateAsync(event.id);
      toast.success(t('webhook_events.toast.marked'));
    } catch (err) {
      const msg =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      toast.error(msg);
    }
  }

  return (
    <Tr data-testid={`webhook-events-row-${event.id}`} data-processed={isProcessed ? 'true' : 'false'}>
      <Td mono>{formatLocalTimestamp(event.created_at, { seconds: true })}</Td>
      <Td emphasis>{event.provider}</Td>
      <Td mono emphasis>
        {event.event_type}
      </Td>
      <Td>
        <Chip tone={isProcessed ? 'neutral' : 'warn'}>
          {isProcessed
            ? t('webhook_events.status.processed')
            : t('webhook_events.status.pending')}
        </Chip>
      </Td>
      <Td>
        <details>
          <summary className="cursor-pointer text-micro text-paper-low">
            {t('webhook_events.payload.expand')}
          </summary>
          <pre
            className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap rounded bg-well p-2 font-mono text-micro text-paper"
            data-testid={`webhook-events-payload-${event.id}`}
          >
            {event.payload}
          </pre>
        </details>
      </Td>
      <Td className="text-right">
        {isProcessed ? (
          <span className="text-micro text-paper-low">—</span>
        ) : (
          <Button
            size="sm"
            onClick={() => void handleMark()}
            loading={mark.isPending}
            data-testid={`webhook-events-mark-${event.id}`}
          >
            {mark.isPending
              ? t('webhook_events.action.marking')
              : t('webhook_events.action.mark_processed')}
          </Button>
        )}
      </Td>
    </Tr>
  );
}
